import store from '@/store'
import useNotifications from '@/composables/useNotifications'
import { hasPermission } from '@/composables/usePermission'
import { useRouter } from '@core/utils/utils'
import i18n from '@/libs/i18n'
import { ref } from '@vue/composition-api'
import { useLambda } from '@/views/habit/awsLambda'
import { oid, date } from '@/helpers/mongoHelpers'
import { createHistoryEntry } from '@/helpers/changeDetection'

export default function useResidualRiskAdd() {
  const { showSuccessMessage, showErrorMessage } = useNotifications()
  const { router } = useRouter()
  const { createItem, updateItem } = useLambda()

  const userData = store.state?.userStore?.userData
  const client_id = userData?.client?.$oid
  const userId = userData?.worker_id?.$oid
  const isSubmitting = ref(false)
  const collection = 'residualAnalysis'
  const arrData = ref({})
  const arrId = ref(null) // Store ID after first draft save

  // Build payload helper
  const buildPayload = (data, status = null) => {
    const payload = {
      client_id: oid(client_id),
      treatment_id: data.treatment_id ? oid(data.treatment_id) : null,
      inherentAnalysis_id: data.inherentAnalysis_id ? oid(data.inherentAnalysis_id) : null,
      // Inherited from ARI (snapshot)
      inheritedP: data.inheritedP || null,
      inheritedI: data.inheritedI || null,
      inheritedRisk: data.inheritedRisk || null,
      inheritedLevel: data.inheritedLevel || '',
      // Controls
      preventiveControls: data.preventiveControls || '',
      preventiveEffectiveness: data.preventiveEffectiveness || 1,
      correctiveControls: data.correctiveControls || '',
      correctiveEffectiveness: data.correctiveEffectiveness || 1,
      // Calculated residual
      residualP: data.residualP || null,
      residualI: data.residualI || null,
      residualRisk: data.residualRisk || null,
      residualLevel: data.residualLevel || '',
      riskReductionPct: data.riskReductionPct || 0,
      // Action plan
      decision: data.decision || null,
      actionPlan: data.actionPlan || '',
      responsible_id: data.responsible_id ? oid(data.responsible_id) : null,
      targetDate: data.targetDate ? date(data.targetDate) : null,
      status: data.status || 'Pendiente',
    }

    // Only update status if explicitly provided
    if (status !== null) {
      payload.status = status
    }

    return payload
  }

  // Save as draft
  const saveDraft = async (data) => {
    try {
      if (!arrId.value) {
        // First save: create document
        const historyEntry = createHistoryEntry(userId, 'create_draft', [])

        const payload = {
          ...buildPayload(data),
          history: [historyEntry],
        }

        const result = await createItem({ collection, payload })

        if (result.insertedId) {
          arrId.value = result.insertedId
        }
      } else {
        // Update existing draft
        const query = { _id: oid(arrId.value) }
        const payload = buildPayload(data)
        const action = { $set: payload }

        await updateItem({ collection, query, action })
      }
    } catch (error) {
      console.error('Error saving draft:', error)
      throw error
    }
  }

  // Final create
  const create = async (data) => {
    if (!hasPermission('privacy-residual-risk', 'create')) {
      showErrorMessage(i18n.t('message.you_are_not_authorized'))
      return
    }
    try {
      isSubmitting.value = true

      if (arrId.value) {
        // Already has draft, just activate it
        const historyEntry = createHistoryEntry(userId, 'activate', [])

        const query = { _id: oid(arrId.value) }
        const payload = buildPayload(data)
        const action = {
          $set: payload,
          $push: { history: historyEntry },
        }

        await updateItem({ collection, query, action })
      } else {
        // Direct creation without draft
        const historyEntry = createHistoryEntry(userId, 'create', [])

        const payload = {
          ...buildPayload(data),
          history: [historyEntry],
        }

        await createItem({ collection, payload })
      }

      showSuccessMessage('ARR creado correctamente')
      router.push({ name: 'privacy-residual-risk-list' })
    } catch (error) {
      console.log(error)
      showErrorMessage('Error al crear ARR')
    } finally {
      isSubmitting.value = false
    }
  }

  return {
    create,
    saveDraft,
    arrData,
    isSubmitting,
  }
}
